import moment, { type Moment } from 'moment';
import type { StylesConfig } from 'react-select';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ReportType =
  | 'TimeTracking'
  | 'CaptureTime'
  | 'MonthlyPerformance'
  | 'Efficiency'
  | 'Productivity'
  | 'Attendance';

export type SortDirection = 'asc' | 'desc' | '';

export interface ApiConfig {
  url: string;
  token: string;
  openInfoWindow?: string;
}

export interface ItemsFilter {
  Id: number;
  Name: string;
}

export interface AppConfig {
  typeReport: ReportType;
  openInfoWindows?: (conv: { ConversationId: string | number }) => void;
  socketConnectionId?: string;
  [key: string]: unknown;
}

export interface TimeTrackingReportProps {
  config: AppConfig;
  customField?: unknown;
}

export interface SelectOption {
  value: string | number;
  label: string | number;
  [key: string]: unknown;
}

export interface AssignedPerson {
  PersonID: number;
  Name: string;
}

export interface TeamItem {
  BasicFieldValueId: number;
  Value: string;
  BasicFieldId: number;
}

export interface ProjectItem {
  ProjectID: number;
  Name: string;
}

export interface LayerItem {
  LayerId: number;
  Name: string;
}

export interface CardTypeItem {
  SideMenuId: number;
  Text: string;
}

export interface DashboardItem {
  DashboardId: number;
  Name: string;
}

export interface StatusItem {
  Id: number;
  Value: string;
}

export interface FavoriteFilter {
  value: string;
  label: string;
  Teams: { Text: string; Items: number[] };
  Employees: { Text: string; Items: number[] };
  Projects: { Text: string; Items: number[] };
  Statuses: { Text: string; Items: number[] };
  CardType: { Text: string; Items: number[] };
}

export interface ChartDataset {
  label: string;
  fill: boolean;
  borderColor: string;
  backgroundColor: string;
  data: number[];
  hidden: boolean;
}

export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

export interface TableRow {
  id?: number;
  personId?: number;
  employee?: string;
  team?: string;
  department?: string;
  artistcategory?: string;
  project?: string;
  milestone?: string;
  cardtype?: string;
  cardname?: string;
  taskname?: string;
  phase?: string;
  phaseName?: string;
  layerName?: string;
  workingdate?: string;
  capturedate?: string;
  capturetime?: string;
  hours?: string;
  externalestimated?: string | number;
  estimated?: string | number;
  actual?: string | number;
  remaining?: string;
  burnt?: string;
  conversationId?: number;
  conversationID?: number;
  loggedHours?: string;
  remainingPercentage?: string;
  month?: string;
  monthId?: number;
  workingcapacity?: number | string;
  loggedhours?: string;
  ptodays?: number | string;
  productivity?: string;
  efficiency?: string;
  productivehours?: string;
  survey?: string;
  sp?: string;
  sptotal?: string;
  missedLogs?: number;
  missedDays?: number;
  delays?: number;
  attendanceId?: number;
  attendanceDate?: string;
  clockIn?: string;
  clockOut?: string;
  commentClockIn?: string;
  commentClockOut?: string;
  showbutton?: boolean;
  progress?: string;
  dates?: Record<string, string>;
  timeouts?: Record<string, number>;
  status?: Record<string, number>;
  customFields?: Record<string, string>;
}

export interface TaskRow {
  id?: number;
  PersonId?: number;
  TaskName?: string;
  Estimated?: number;
  Remaining?: number;
  LoggedHours?: number;
  ProductiveHrs?: number;
  RemainingInitial?: number;
  RemainingFinal?: number;
  Link?: string;
  conversationID?: string;
}

// ─── Pure helpers (outside component, never recreated) ─────────────────────────

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function getMonthsForYear(year: number): string[]
{
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  if (year === currentYear) return MONTHS.slice(0, currentMonth + 1);
  if (year < currentYear) return MONTHS;
  return [];
}

export function getTimeZoneOffset(): string
{
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  return `${sign}${Math.abs(Math.floor(offsetHours))}`;
}

export function getStartAndEndOfWeek(date: Date): {
  startOfWeek: string;
  endOfWeek: string;
}
{
  const start = new Date(date);
  const end = new Date(date);
  const diff = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - diff);
  end.setDate(start.getDate() + 6);
  start.setUTCHours(18, 0, 0, 0);
  end.setUTCHours(18, 0, 0, 0);
  return { startOfWeek: start.toISOString(), endOfWeek: end.toISOString() };
}

export function formatDateString(dateString: string, includeTime: boolean): string
{
  if (!dateString || dateString === '0001-01-01T00:00:00') return '';
  const str = dateString.replace('Z', '');
  const d = new Date(str);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const y = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');
  return includeTime ? `${m}-${day}-${y} ${h}:${min}:${sec}` : `${m}-${day}-${y}`;
}

export function formatTimeString(dateString: string): string
{
  if (!dateString || dateString === '0001-01-01T00:00:00') return '';
  const d = new Date(dateString.replace('Z', ''));
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return ` ${h}:${m}:${s}`;
}

export function getTimeInMilliseconds(timeString: string): number
{
  const match = timeString.match(/(\d{2}):(\d{2}):(\d{2}) ([AP]M)/);
  if (!match) return 0;
  const h = parseInt(match[1]!, 10);
  const isAm = match[4] === 'AM';
  const adjustedH = h === 12 ? (isAm ? 0 : 12) : h + (isAm ? 0 : 12);
  return new Date(0).setUTCHours(adjustedH, parseInt(match[2]!, 10), parseInt(match[3]!, 10));
}

export function getEmptyString(input: string | null | undefined): string
{
  return !input || input.trim() === '' ? 'N/A' : input;
}

export function cleanText(html: string): string
{
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.innerText;
}

export function buildIdList(ids: (string | number)[]): string
{
  return ids.join(',');
}

// Header label helpers
export function efficiencyGetTimeoutHeader(key: string): string
{
  const toTitle = (s: string) =>
    s
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  const cleaned = key.replaceAll('_', ' ').toLowerCase().trim();
  const [, ...rest] = cleaned.split('timeout');
  const suffix = rest.join(' ').trim();
  return `Time Out${suffix ? ' ' + toTitle(suffix) : ''}`;
}

export function efficiencyGetStatusHeader(key: string): string
{
  const suffix = key.replaceAll('s_', '');
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

export function efficiencyGetCustomFieldHeader(key: string): string
{
  const suffix = key.replaceAll('cf', '').replaceAll('_', ' ');
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

// Data extraction helpers
export function getTimeouts(results: Record<string, unknown>): Record<string, string>
{
  return Object.fromEntries(
    Object.entries(results)
      .filter(([k]) => k.includes('timeout') || k.includes('block'))
      .map(([k, v]) => [k, parseFloat(String(v)).toFixed(2)]),
  );
}

export function getStatus(results: Record<string, unknown>): Record<string, string>
{
  return Object.fromEntries(
    Object.entries(results)
      .filter(([k]) => k.includes('s_'))
      .map(([k, v]) => [k, parseFloat(String(v)).toFixed(0)]),
  );
}

export function getCustomFields(results: Record<string, unknown>): Record<string, string>
{
  return Object.fromEntries(
    Object.entries(results)
      .filter(([k]) => k.includes('cf_'))
      .map(([k, v]) => [k.replace('_', ' '), cleanText(String(v ?? ''))]),
  );
}

export function getTotals(rows: Record<string, unknown>[]): Record<string, number>
{
  return rows.reduce<Record<string, number>>((acc, row) =>
  {
    for (const [k, v] of Object.entries(row))
    {
      acc[k] = (acc[k] ?? 0) + parseFloat(String(v));
    }
    return acc;
  }, {});
}

export function getDates(results: Record<string, unknown>): Record<string, string>
{
  return Object.fromEntries(
    Object.entries(results)
      .filter(([k]) => /^\d{4}-\d{2}-\d{2}$/.test(k))
      .map(([k, v]) => [k, v === null ? '0.00' : parseFloat(String(v)).toFixed(2)]),
  );
}

export function getMissedLogs(results: Record<string, unknown>, hireDate: Date): number
{
  const dateKeys = Object.keys(results).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k));
  let missed = 0;
  for (const k of dateKeys)
  {
    const cur = new Date(k);
    const val = Number(results[k] ?? 0);
    if (cur.getUTCDay() !== 0 && cur.getUTCDay() !== 6 && cur >= hireDate)
    {
      if (val < 8) missed++;
    }
  }
  return missed;
}

// Summary helpers
export function efficiencyGetProjects(table: Array<Record<string, unknown>>): string
{
  return Array.from(
    new Set(
      table.flatMap(item =>
        (!item.Projects ? [] : String(item.Projects).split(',')).map(s => s.trim()),
      ),
    ),
  ).join(', ');
}

export function efficiencyGetResources(table: Array<Record<string, unknown>>): string
{
  return Array.from(new Set(table.map(p => String(p.Employees)))).join(', ');
}

export function efficiencyGetDateRange(start: string, due: string): string
{
  const fmt = (s: string) => s.replace(/(\d{4})(\d{2})(\d{2})/, '$2/$3/$1');
  return `${fmt(start)} - ${fmt(due)}`;
}

export function efficiencyGetCapacityHours(capacity: Array<Record<string, number>>): string
{
  const wc = capacity.reduce((a, p) => a + p.WorkingCapacity!, 0);
  const pto = capacity.reduce((a, p) => a + p.PTODays!, 0) * 8;
  return `${(wc + pto).toFixed(2)} hours`;
}

export function efficiencyGetWorkingCapacity(capacity: Array<Record<string, number>>): string
{
  return `${capacity.reduce((a, p) => a + p.WorkingCapacity!, 0).toFixed(2)} hours`;
}

export function efficiencyGetPercentage(rows: Array<Record<string, number>>): string
{
  const sum = rows.reduce((a, p) => a + p.Efficiency!, 0);
  return `${(sum / rows.length).toFixed(2)}%`;
}

export function efficiencyGetBurned(rows: Array<Record<string, number>>): string
{
  return `${rows.reduce((a, p) => a + p.BurnedHours!, 0).toFixed(2)} hours`;
}

export function productivityGetPercentage(rows: Array<Record<string, number>>): string
{
  const ph = rows.reduce((a, p) => a + p.ProductiveHours!, 0);
  const wc = rows.reduce((a, p) => a + p.WorkingCapacity!, 0);
  return `${((ph * 100) / wc).toFixed(2)}%`;
}

export function productivityGetTotalPercentage(rows: Array<Record<string, number>>): string
{
  const sp = rows.reduce((a, p) => a + p.SP!, 0);
  const total = rows.reduce((a, p) => a + p.SPTotal!, 0);
  return `${((sp * 100) / total).toFixed(2)}%`;
}

export function productivityGetStoryPoints(rows: Array<Record<string, number>>): string
{
  return `${rows.reduce((a, p) => a + p.SP!, 0).toFixed(2)} Points`;
}

export function productivityGetTotalStoryPoints(rows: Array<Record<string, number>>): string
{
  return `${rows.reduce((a, p) => a + p.SPTotal!, 0).toFixed(2)} Points`;
}

export function productivityGetHours(rows: Array<Record<string, number>>): string
{
  return `${rows.reduce((a, p) => a + p.ProductiveHours!, 0).toFixed(2)} hours`;
}

// Sort helpers
type SortFn = (a: TableRow, b: TableRow) => number;

function strCmp(a: string | undefined, b: string | undefined): number
{
  if (!a) return -1;
  if (!b) return 1;
  return a.localeCompare(b);
}

function numCmp(a: unknown, b: unknown): number
{
  return Number(a) - Number(b);
}

function naNumCmp(a: unknown, b: unknown): number
{
  const parse = (v: unknown) => (v === 'N/A' ? -Infinity : Number(v));
  return parse(a) - parse(b);
}

export function buildSortFn(column: string, dir: SortDirection): SortFn
{
  const flip = dir === 'desc' ? -1 : 1;

  const sortMap: Record<string, SortFn> = {
    month: (a, b) => flip * strCmp(a.month, b.month),
    personId: (a, b) => flip * numCmp(a.personId, b.personId),
    employee: (a, b) => flip * strCmp(a.employee, b.employee),
    team: (a, b) => flip * strCmp(a.team, b.team),
    department: (a, b) => flip * strCmp(a.department, b.department),
    artistcategory: (a, b) => flip * strCmp(a.artistcategory, b.artistcategory),
    missedLogs: (a, b) => flip * numCmp(a.missedLogs, b.missedLogs),
    project: (a, b) => flip * strCmp(a.milestone, b.milestone),
    'project-captured': (a, b) => flip * strCmp(a.project, b.project),
    card_type: (a, b) => flip * strCmp(a.cardtype, b.cardtype),
    card_name: (a, b) => flip * strCmp(a.cardname, b.cardname),
    taskname: (a, b) => flip * strCmp(a.taskname, b.taskname),
    layer: (a, b) => flip * strCmp(a.layerName, b.layerName),
    phase_name: (a, b) => flip * strCmp(a.phaseName, b.phaseName),
    phase: (a, b) => flip * strCmp(a.phase, b.phase),
    workingdate: (a, b) =>
      flip * (Date.parse(a.workingdate ?? '') - Date.parse(b.workingdate ?? '')),
    capturedate: (a, b) =>
      flip * (Date.parse(a.capturedate ?? '') - Date.parse(b.capturedate ?? '')),
    capturetime: (a, b) =>
      flip *
      (getTimeInMilliseconds(a.capturetime ?? '') - getTimeInMilliseconds(b.capturetime ?? '')),
    log_date: (a, b) =>
      flip *
      (Date.parse((a as Record<string, string>).hourlogdate ?? '') -
        Date.parse((b as Record<string, string>).hourlogdate ?? '')),
    hours: (a, b) => flip * numCmp(a.hours, b.hours),
    efficiencypercentage: (a, b) => flip * numCmp(a.remainingPercentage, b.remainingPercentage),
    estimated: (a, b) => flip * naNumCmp(a.estimated, b.estimated),
    remaining: (a, b) => flip * numCmp(a.remaining, b.remaining),
    actual: (a, b) => flip * numCmp(a.actual, b.actual),
    progress: (a, b) => flip * naNumCmp(a.progress, b.progress),
    missedDays: (a, b) => flip * numCmp(a.missedDays, b.missedDays),
    delays: (a, b) => flip * numCmp(a.delays, b.delays),
    loggedhours: (a, b) => flip * numCmp(a.loggedhours, b.loggedhours),
    workingcapacity: (a, b) => flip * numCmp(a.workingcapacity, b.workingcapacity),
    ptodays: (a, b) => flip * numCmp(a.ptodays, b.ptodays),
    productivity: (a, b) => flip * numCmp(a.productivity, b.productivity),
    efficiency: (a, b) => flip * numCmp(a.efficiency, b.efficiency),
    logged_hours: (a, b) => flip * numCmp(a.loggedHours, b.loggedHours),
    over_budget: (a, b) => flip * numCmp(a.burnt, b.burnt),
    survey: (a, b) => flip * numCmp(a.survey, b.survey),
    sp: (a, b) => flip * numCmp(a.sp, b.sp),
    productivehours: (a, b) => flip * numCmp(a.productivehours, b.productivehours),
    externalestimated: (a, b) => flip * naNumCmp(a.externalestimated, b.externalestimated),
    attendanceDate: (a, b) => flip * strCmp(a.attendanceDate, b.attendanceDate),
    clockIn: (a, b) => flip * strCmp(a.clockIn, b.clockIn),
    clockOut: (a, b) => flip * strCmp(a.clockOut, b.clockOut),
    commentClockIn: (a, b) => flip * strCmp(a.commentClockIn, b.commentClockIn),
    commentClockOut: (a, b) => flip * strCmp(a.commentClockOut, b.commentClockOut),
  };

  // Dynamic column types
  if (/timeout|block/.test(column))
  {
    return (a, b) => flip * numCmp(a.timeouts?.[column], b.timeouts?.[column]);
  }
  if (column.includes('s_'))
  {
    return (a, b) => flip * numCmp(a.status?.[column], b.status?.[column]);
  }
  if (column.includes('cf'))
  {
    return (a, b) => flip * strCmp(a.customFields?.[column], b.customFields?.[column]);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(column))
  {
    return (a, b) => flip * numCmp(a.dates?.[column], b.dates?.[column]);
  }

  return sortMap[column] ?? (() => 0);
}

export function buildModalSortFn(
  column: string,
  dir: SortDirection,
): (a: TaskRow, b: TaskRow) => number
{
  const flip = dir === 'desc' ? -1 : 1;
  const modalMap: Record<string, (a: TaskRow, b: TaskRow) => number> = {
    name: (a, b) => flip * strCmp(a.TaskName, b.TaskName),
    estimated: (a, b) => flip * numCmp(a.Estimated, b.Estimated),
    remaining: (a, b) => flip * numCmp(a.Remaining, b.Remaining),
    loggedHours: (a, b) => flip * numCmp(a.LoggedHours, b.LoggedHours),
    productiveHrs: (a, b) => flip * numCmp(a.ProductiveHrs, b.ProductiveHrs),
    remainingInitial: (a, b) => flip * numCmp(a.RemainingInitial, b.RemainingInitial),
    remainingFinal: (a, b) => flip * numCmp(a.RemainingFinal, b.RemainingFinal),
  };
  return modalMap[column] ?? (() => 0);
}
export function toMoment(date: string | null): Moment | null
{
  return date ? moment(date, 'YYYY-MM-DD') : null;
}

export function toStringDate(date: Moment | null): string
{
  return date ? date.format('YYYY-MM-DD') : '';
}

export const comboBoxSelectStyles: StylesConfig<any, false> = {
  control: base => ({
    ...base,
    minHeight: 30,
    fontSize: 11,
    borderColor: '#ccc',
    boxShadow: 'none',
    width: '220px',
    '&:hover': { borderColor: '#aaa' },
    position: 'relative',
    paddingRight: 30,
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    zIndex: 10,
  }),
  multiValueLabel: base => ({
    ...base,
    fontSize: '10px',
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: '#fff',
    borderColor: '#ccc',
    borderWidth: '1px',
  }),
  multiValueRemove: base => ({
    ...base,
    fontSize: '4px',
    color: '#ccc',
    ':hover': {
      backgroundColor: '#fff',
    },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 12,
    padding: '5px 12px',
    backgroundColor: state.isSelected ? '#62accb77' : state.isFocused ? '#62accb' : undefined,
    color: state.isSelected || state.isFocused ? '#fff' : '#333',
    cursor: 'pointer',
  }),
  valueContainer: (base, _state) => ({
    ...base,
    maxHeight: '120px',
    overflowY: 'auto',
    flexWrap: 'wrap',
  }),
  indicatorsContainer: base => ({
    ...base,
    position: 'absolute',
    right: 0,
    top: 0,
  }),
  clearIndicator: base => ({
    ...base,
    padding: 4,
    color: '#ccc',
  }),
};
